import { ArrowLeft, Atom, Flame, LineChart, Microscope, Scale, TestTube } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { CalibrationModeView } from './InstrumentalAnalysisCalibration';
import { FlameMode } from './InstrumentalAnalysisFlame';
import { GravimetricMode } from './InstrumentalAnalysisGravimetric';
import { IonsMode } from './InstrumentalAnalysisIons';
import {
  INSTRUMENTAL_ANALYSIS_TOOL_IDENTITY,
  INSTRUMENTAL_MODES,
  INSTRUMENTAL_MODE_YEARS,
  type InstrumentalMode,
} from './instrumentalAnalysis';
import { StudyShell } from './StudyShell';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';

const MODE_COPY: Record<
  InstrumentalMode,
  { tab: string; eyebrow: string; title: string; description: string }
> = {
  flame: {
    tab: 'Flame tests',
    eyebrow: 'Flame tests and emission spectra',
    title: 'Which metal ion is in the flame?',
    description:
      'Predict a flame colour, then see the line spectrum and the electron jump that produces it.',
  },
  ions: {
    tab: 'Ion tests',
    eyebrow: 'Qualitative analysis',
    title: 'Plan tests to identify an unknown ion',
    description:
      'Choose tests and their order. The model reports observations and flags interference.',
  },
  colorimetry: {
    tab: 'Colorimetry',
    eyebrow: 'Beer–Lambert calibration',
    title: 'Find a concentration from absorbance',
    description:
      'Measure standards, fit a calibration line and read the unknown, but only inside the range.',
  },
  aas: {
    tab: 'AAS',
    eyebrow: 'Atomic absorption spectroscopy',
    title: 'Measure one element in a mixture',
    description:
      'Calibrate at the element’s own wavelength and read a metal concentration in ppm.',
  },
  gravimetric: {
    tab: 'Gravimetric',
    eyebrow: 'Gravimetric analysis',
    title: 'Weigh a precipitate to find % mass',
    description:
      'Precipitate one ion, dry it to constant mass, then use the mole ratio to find the analyte.',
  },
};

const MODE_ICONS = {
  flame: Flame,
  ions: TestTube,
  colorimetry: LineChart,
  aas: Atom,
  gravimetric: Scale,
} as const;

export function InstrumentalAnalysisLabPage() {
  const year = useChemistryToolYear();
  const [mode, setMode] = useState<InstrumentalMode>(() =>
    modeForYear(INSTRUMENTAL_MODE_YEARS, year),
  );
  const copy = MODE_COPY[mode];
  const ModeIcon = MODE_ICONS[mode];

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'instrumental-analysis', state: { mode } }}>
      <Link
        to="/learn/study/subjects/chemistry/tools"
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry tools
      </Link>

      <header
        className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-sky/25 bg-gradient-to-br from-wash-sky via-canvas-pure to-wash-mint p-6 shadow-card-soft lg:p-8"
        data-tool-key={INSTRUMENTAL_ANALYSIS_TOOL_IDENTITY.toolKey}
        data-tool-version={INSTRUMENTAL_ANALYSIS_TOOL_IDENTITY.toolVersion}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-sky">
              <Microscope className="h-4 w-4" aria-hidden="true" /> Analysis and separation
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Instrumental Analysis Lab
            </h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              Identify ions from flame colours, line spectra and test-tube observations, then
              measure concentration with colorimetry, atomic absorption and gravimetric analysis.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            {year !== null ? `Year ${year} view` : 'Year 11–12'} · five modes
          </div>
        </div>

        <div
          className="mt-6 grid max-w-4xl grid-cols-2 gap-1 rounded-[1.5rem] bg-white/75 p-1 sm:grid-cols-5 sm:rounded-full"
          role="group"
          aria-label="Choose analysis mode"
        >
          {INSTRUMENTAL_MODES.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={mode === option}
              onClick={() => setMode(option)}
              className={`rounded-full px-3 py-2.5 text-sm font-extrabold transition-colors ${
                mode === option ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white'
              }`}
            >
              {MODE_COPY[option].tab}
            </button>
          ))}
        </div>
      </header>

      <section className="my-6 rounded-[1.75rem] border border-hairline bg-canvas-pure px-5 py-4 shadow-card-soft sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
              {copy.eyebrow}
            </p>
            <p className="mt-1 text-xl font-extrabold text-ink">{copy.title}</p>
            <p className="mt-1 text-sm font-medium leading-6 text-slate2">{copy.description}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2 rounded-full bg-wash-sunshine px-4 py-2 text-xs font-extrabold text-ink-soft">
            <ModeIcon className="h-4 w-4" aria-hidden="true" />
            Explore only · not mastery evidence
          </div>
        </div>
      </section>

      {mode === 'flame' ? <FlameMode /> : null}
      {mode === 'ions' ? <IonsMode /> : null}
      {mode === 'colorimetry' ? <CalibrationModeView key="colorimetry" mode="colorimetry" /> : null}
      {mode === 'aas' ? <CalibrationModeView key="aas" mode="aas" /> : null}
      {mode === 'gravimetric' ? <GravimetricMode /> : null}

      <footer className="my-6 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-xs font-medium leading-5 text-slate2">
        This tool uses reviewed, bounded teaching cases with generated data. It does not replace a
        supervised practical, determine laboratory safety, save an assessment attempt or claim
        alignment to a state syllabus. Flame tests use toxic and flammable materials and need a
        teacher-approved risk assessment. Gravimetric reagents such as barium chloride (toxic) and
        silver nitrate (corrosive, stains skin) and furnace ignition need the same supervision.
      </footer>
    </StudyShell>
  );
}
